import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Segmented, Typography, message } from "antd";
import {
    ACORN_BRAND,
    createDefaultAcornFormState,
} from "./acornQuoteData";
import { ACORN_QUOTE_MESSAGES } from "./acornQuoteStrings";
import { buildAcornQuoteDisplay, validateAcornQuoteForm } from "./acornQuoteFormat";
import AcornQuoteForm from "./AcornQuoteForm";
import AcornQuotePoster from "./AcornQuotePoster";
import { captureAcornQuotePoster, ensureAcornPosterFontsLoaded } from "./acornQuoteCapture";
import {
    canUseAcornShareSheet,
    createAcornPosterAsset,
    downloadAcornPoster,
    makeAcornFilename,
    openAcornPoster,
    shareAcornPoster,
} from "./acornQuoteSave";
import "./acornQuote.css";

const { Paragraph } = Typography;

const ACORN_LOCALE_KEY = "wild_acorn_quote_locale";

function getInitialAcornLocale() {
    try {
        return window.localStorage.getItem(ACORN_LOCALE_KEY) === "en" ? "en" : "zh";
    } catch {
        return "zh";
    }
}

function shouldUsePosterOverlay() {
    if (typeof window === "undefined") {
        return false;
    }

    const ua = navigator.userAgent;
    const touchDevice = /iPhone|iPad|iPod|Android/i.test(ua);
    return touchDevice || window.matchMedia("(max-width: 700px)").matches;
}

function AcornQuotePage() {
    const [locale, setLocale] = useState(getInitialAcornLocale);
    const [formState, setFormState] = useState(() => createDefaultAcornFormState());
    const [style, setStyle] = useState("classic");
    const [validationError, setValidationError] = useState("");
    const [posterReady, setPosterReady] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [overlayAsset, setOverlayAsset] = useState(null);
    const [overlayNotice, setOverlayNotice] = useState("");

    const posterRef = useRef(null);
    const assetRef = useRef(null);

    const t = useMemo(() => {
        const messages = ACORN_QUOTE_MESSAGES[locale] || ACORN_QUOTE_MESSAGES.zh;
        return (key) =>
            key.split(".").reduce((current, part) => {
                if (current == null) {
                    return undefined;
                }
                return current[part];
            }, messages);
    }, [locale]);

    const quoteDisplay = useMemo(() => buildAcornQuoteDisplay(formState), [formState]);
    const canShare = useMemo(() => canUseAcornShareSheet(), []);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${ACORN_BRAND} | ${t("page.title")}`;
        return () => {
            document.title = previousTitle;
        };
    }, [t]);

    useEffect(() => {
        let active = true;
        ensureAcornPosterFontsLoaded().finally(() => {
            if (active) {
                setPosterReady(true);
            }
        });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        return () => {
            if (assetRef.current) {
                URL.revokeObjectURL(assetRef.current.url);
            }
        };
    }, []);

    const changeLocale = useCallback(() => {
        const next = locale === "zh" ? "en" : "zh";
        setLocale(next);
        try {
            window.localStorage.setItem(ACORN_LOCALE_KEY, next);
        } catch {
            // Storage can be unavailable in private browsing; the in-memory locale still works.
        }
    }, [locale]);

    const closeOverlay = useCallback(() => {
        setOverlayAsset(null);
        setOverlayNotice("");
    }, []);

    const resetForm = useCallback(() => {
        setFormState(createDefaultAcornFormState());
        setValidationError("");
        if (assetRef.current) {
            URL.revokeObjectURL(assetRef.current.url);
            assetRef.current = null;
        }
        closeOverlay();
        message.success(t("page.resetDone"));
    }, [closeOverlay, t]);

    const handleGenerate = useCallback(async () => {
        const error = validateAcornQuoteForm(formState, t);
        setValidationError(error || "");
        if (error) {
            message.error(error);
            return;
        }

        setGenerating(true);
        try {
            await ensureAcornPosterFontsLoaded();
            const canvas = await captureAcornQuotePoster(posterRef.current, style);
            const filename = makeAcornFilename(formState.projectName, quoteDisplay.quoteDateLabel);

            if (!shouldUsePosterOverlay()) {
                downloadAcornPoster(canvas, filename);
                message.success(t("page.generated"));
                return;
            }

            const asset = await createAcornPosterAsset(canvas, filename);
            if (assetRef.current) {
                URL.revokeObjectURL(assetRef.current.url);
            }
            assetRef.current = asset;
            setOverlayAsset(asset);
            setOverlayNotice("");
        } catch (error) {
            console.error("Acorn poster generation failed:", error);
            message.error(t("page.failed"));
        } finally {
            setGenerating(false);
        }
    }, [formState, quoteDisplay.quoteDateLabel, style, t]);

    const handleShare = useCallback(async () => {
        if (!overlayAsset) {
            return;
        }

        try {
            const result = await shareAcornPoster(
                overlayAsset,
                `${ACORN_BRAND} ${t("page.title")}`
            );
            if (result === "unsupported") {
                setOverlayNotice(t("overlay.unsupportedShare"));
            }
        } catch (error) {
            console.error("Acorn poster sharing failed:", error);
            setOverlayNotice(t("overlay.unsupportedShare"));
        }
    }, [overlayAsset, t]);

    const handleOpenImage = useCallback(() => {
        if (overlayAsset) {
            openAcornPoster(overlayAsset);
        }
    }, [overlayAsset]);

    const styleOptions = [
        { label: t("style.classic"), value: "classic" },
        { label: t("style.editorial"), value: "editorial" },
    ];

    return (
        <main className="acorn-page">
            <header className="acorn-header">
                <div className="acorn-header__brand">
                    <span className="acorn-header__name">{ACORN_BRAND}</span>
                    <span className="acorn-header__tagline">{t("page.brandTagline")}</span>
                </div>
                <Button size="small" onClick={changeLocale}>
                    {locale === "zh" ? "EN" : "中文"}
                </Button>
            </header>

            <section className="acorn-intro">
                <h1 className="acorn-intro__title">{t("page.title")}</h1>
                <Paragraph className="acorn-intro__subtitle">{t("page.subtitle")}</Paragraph>
            </section>

            <div className="acorn-layout">
                <div className="acorn-layout__form">
                    <AcornQuoteForm
                        formState={formState}
                        onChange={setFormState}
                        validationError={validationError}
                        t={t}
                    />
                </div>

                <div className="acorn-layout__preview">
                    <div className="acorn-preview-toolbar">
                        <div className="acorn-preview-toolbar__heading">
                            <span className="acorn-preview-toolbar__label">{t("style.heading")}</span>
                            <Segmented
                                value={style}
                                onChange={setStyle}
                                options={styleOptions}
                            />
                        </div>
                        <div className="acorn-preview-toolbar__actions">
                            <Button
                                type="primary"
                                loading={generating}
                                disabled={!posterReady}
                                onClick={handleGenerate}
                            >
                                {generating ? t("page.generating") : t("page.generate")}
                            </Button>
                            <Button onClick={resetForm}>{t("page.reset")}</Button>
                        </div>
                    </div>

                    <div className="acorn-preview-stage">
                        {!posterReady && (
                            <p className="acorn-preview-stage__status">{t("page.generating")}</p>
                        )}
                        <div className={posterReady ? "" : "acorn-preview-stage--loading"}>
                            <AcornQuotePoster
                                ref={posterRef}
                                quoteDisplay={quoteDisplay}
                                style={style}
                                t={t}
                                locale={locale}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {overlayAsset && (
                <div
                    className="acorn-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={closeOverlay}
                >
                    <div className="acorn-overlay__panel" onClick={(event) => event.stopPropagation()}>
                        <p className="acorn-overlay__hint">
                            {overlayNotice || t("overlay.shareHint")}
                        </p>
                        <img
                            className="acorn-overlay__image"
                            src={overlayAsset.url}
                            alt={`${ACORN_BRAND} ${t("page.title")}`}
                            draggable={false}
                        />
                        <div className="acorn-overlay__actions">
                            {canShare && (
                                <Button type="primary" onClick={handleShare}>
                                    {t("overlay.saveToPhotos")}
                                </Button>
                            )}
                            <Button onClick={handleOpenImage}>{t("overlay.openImage")}</Button>
                            <Button onClick={closeOverlay}>{t("overlay.close")}</Button>
                        </div>
                        <p className="acorn-overlay__fallback">{t("overlay.longPressHint")}</p>
                    </div>
                </div>
            )}
        </main>
    );
}

export default AcornQuotePage;
